import React, { useState } from 'react';
import { useCookieConsent } from '../context/CookieContext';
import { useLanguage } from '../context/LanguageProvider';
import { CookieIcon } from '../components/icons/CookieIcon';
import { SettingsIcon } from '../components/icons/SettingsIcon';
import cs from '../lang/cs.json';
import de from '../lang/de.json';
import en from '../lang/en.json';
import fr from '../lang/fr.json';
import it from '../lang/it.json';
import pl from '../lang/pl.json';
import es from '../lang/es.json';
import ro from '../lang/ro.json';
import nl from '../lang/nl.json';
import type { Language } from '../context/LanguageProvider';

const CookieModal = () => {
  const { consent, setConsent, isConsentSet } = useCookieConsent();
  const [analyticsAllowed, setAnalyticsAllowed] = useState(consent.analytics);
  const [marketingAllowed, setMarketingAllowed] = useState(consent.marketing);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cookieConsentGiven') === 'true';
    }
    return false;
  });
  const [showSettings, setShowSettings] = useState(false);

  const { language } = useLanguage();
  const translations: Record<Language, any> = {
    en, cs, de, fr, it, pl, es, ro, nl
  };
  const t = translations[language] || en;

  if (isConsentSet || dismissed) return null;

  // Helper to set consent and dismiss modal
  const handleDecision = (newConsent: { necessary: true; analytics: boolean; marketing: boolean }) => {
    setConsent(newConsent);
    setDismissed(true);
  };

  const handleReject = () => {
    handleDecision({ necessary: true, analytics: false, marketing: false });
  };

  const handleAcceptChosen = () => {
    handleDecision({ necessary: true, analytics: analyticsAllowed, marketing: marketingAllowed });
  };

  const handleAcceptAll = () => {
    handleDecision({ necessary: true, analytics: true, marketing: true });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-4 py-6">
      <div className="bg-white rounded-[16px] p-6 max-w-full sm:max-w-[678px] w-full shadow-lg text-black">
        <h2 className="text-[32px] leading-[40px] font-bold font-pangaia text-[#1C1C1E] flex gap-2 flex-nowrap items-center"><CookieIcon/> {t.cookiesTitle || 'Cookies & Consent'}</h2>
        <p className="text-[16px] leading-[24px] font-pangram text-[#636366] py-[24px] ">
          {t.cookiesDescription || "We use cookies to improve your experience, analyze traffic, and serve personalized ads. Choose which types of cookies you'd like to allow."}
        </p>
        {showSettings && (
          <div className="flex flex-row gap-6 text-sm mb-4 mt-4 items-start sm:items-center justify-start flex-col sm:flex-row">
            <label className="flex items-center gap-2 text-black cursor-pointer">
              <span className="relative inline-block w-10 h-6">
                <input
                  type="checkbox"
                  checked={analyticsAllowed}
                  onChange={e => setAnalyticsAllowed(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-full h-full bg-[#D1D1D6] rounded-full peer-checked:bg-[#6195FF] transition-all duration-300"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-md transition-all duration-300 peer-checked:translate-x-4"></div>
              </span>
              {t.allowAnalytics || 'Allow analytics cookies'}
            </label>
            <label className="flex items-center gap-2 text-black cursor-pointer">
              <span className="relative inline-block w-10 h-6">
                <input
                  type="checkbox"
                  checked={marketingAllowed}
                  onChange={e => setMarketingAllowed(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-full h-full bg-[#D1D1D6] rounded-full peer-checked:bg-[#6195FF] transition-all duration-300"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-md transition-all duration-300 peer-checked:translate-x-4"></div>
              </span>
              {t.allowMarketing || 'Allow marketing cookies'}
            </label>
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-end items-center gap-4 mt-4">
          <div className="grid grid-cols-2 sm:flex gap-2 justify-end items-center w-full">
            <button
              type="button"
              onClick={() => setShowSettings(prev => !prev)}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-transparent text-black text-sm font-pangram hover:opacity-90 transition-opacity duration-200 col-span-2 sm:col-span-1"
            >
              <SettingsIcon />
              {t.settings || 'Settings'}
            </button>
            <button
              type="button"
              onClick={handleReject}
              className="px-6 py-2 rounded-full border border-[#C7C7CC] text-[#1C1C1E] font-pangram text-[16px] leading-6 flex items-center gap-2 hover:opacity-90 transition-opacity duration-200"
            >
              {t.reject || 'Reject All'}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6" cy="6" r="6" fill="#6195FF"/>
              </svg>
            </button>
            {showSettings && (
              <button
                type="button"
                onClick={handleAcceptChosen}
                className="px-6 py-2 rounded-full border border-[#C7C7CC] text-[#1C1C1E] font-pangram text-[16px] leading-6 flex items-center gap-2 hover:opacity-90 transition-opacity duration-200"
              >
                {t.acceptChosen || 'Accept Chosen'}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="6" cy="6" r="6" fill="#6195FF"/>
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={handleAcceptAll}
              className="px-6 py-2 rounded-full bg-[#1C1C1E] text-white font-pangram text-[16px] leading-6 flex items-center gap-2 hover:opacity-90 transition-opacity duration-200"
            >
              {t.acceptAll || 'Accept All'}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6" cy="6" r="6" fill="#6195FF"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieModal;